import React, { FC, KeyboardEvent, useEffect } from "react";
import { defineMessages, useIntl } from "react-intl";
import Svg from "@natera/material/lib/svg";
import aiAssistant from "@app/pippa/assets/ai-assistant.png";
import ArrowUp from "@app/pippa/assets/svg/arrow-up.svg";
import Pattern from "@app/pippa/assets/svg/pattern.svg";
import { AvatarLogo } from "@app/pippa/components/message/avatarLogo";
import { usePippaAnalytics } from "@app/pippa/hooks/usePippaAnalytics";
import { KeyTypes } from "@app/pippa/models";
import { PippaIngressSource } from "@app/pippa/models/analytics";
import "./widgetBanner.scss";

const messages = defineMessages({
  widgetBannerHeader: {
    id: "widgetBannerHeader",
    defaultMessage: "Natera Results Assistant",
  },
  widgetBannerMessage: {
    id: "widgetBannerMessage",
    defaultMessage:
      "Let me help you understand your Signatera results step by step.",
  },
  widgetBannerInputPlaceholder: {
    id: "widgetBannerInputPlaceholder",
    defaultMessage: "Ask me anything",
  },
  widgetBannerInputAriaLabel: {
    id: "widgetBannerInputAriaLabel",
    defaultMessage: "Open the Natera Results Assistant",
  },
});

interface WidgetBannerProps {
  showWidgetBanner: boolean;
  onButtonClickHandler: () => void;
  isWidgetOpen?: boolean;
}

export const WidgetBanner: FC<WidgetBannerProps> = ({
  showWidgetBanner,
  onButtonClickHandler,
  isWidgetOpen = false,
}) => {
  const intl = useIntl();
  const { trackIngress, trackBannerShown } = usePippaAnalytics();

  // An open widget (e.g. auto-opened after the PDF) covers the banner, so it
  // only counts as shown once the patient can actually see it.
  const isBannerVisible = showWidgetBanner && !isWidgetOpen;

  useEffect(() => {
    if (isBannerVisible) {
      trackBannerShown();
    }
  }, [isBannerVisible]);

  if (!showWidgetBanner) {
    return null;
  }

  const handleClick = () => {
    trackIngress(PippaIngressSource.AI_ASSISTANT_BANNER);
    onButtonClickHandler();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const currentKey = e.key.toLowerCase();
    if (currentKey === KeyTypes.ENTER || currentKey === KeyTypes.SPACE) {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      className="widget-banner"
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={intl.formatMessage(messages.widgetBannerInputAriaLabel)}
    >
      <h2 className="widget-banner-title">
        {intl.formatMessage(messages.widgetBannerHeader)}
      </h2>

      <div className="widget-banner__message">
        <AvatarLogo logoSrc={aiAssistant} />
        <div className="widget-banner__bubble">
          <p className="widget-banner__bubble-text">
            {intl.formatMessage(messages.widgetBannerMessage)}
          </p>
        </div>
      </div>

      <div className="widget-banner__input" aria-hidden={true}>
        <span className="widget-banner__input-placeholder">
          {intl.formatMessage(messages.widgetBannerInputPlaceholder)}
        </span>
        <span className="widget-banner__send">
          <Svg content={ArrowUp} />
        </span>
      </div>

      <span className="widget-banner__bg-bar" aria-hidden={true} />
      <Svg
        className="widget-banner__bg-pattern"
        content={Pattern}
        aria-hidden={true}
      />
    </div>
  );
};
